import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OrganizationTo } from '../shared/models/organizationTo';
import { MapProfile, PsapMapProfile } from '../shared/models/mapProfile';
import { EsriMapService } from './esri-map.service';
import { OrganizationsService } from '../organizations/organizations.service';
import { HomeService } from '../home/home.service';
import { SharedService } from '../shared/shared.service';
import { Roles } from '../shared/enums/roles';
import { Alerts } from '../shared/enums/alerts';

@Component({
    selector: 'app-map-configuration',
    templateUrl: './map-configuration.component.html',
    styleUrls: ['./map-configuration.component.scss']
})
export class MapConfigurationComponent implements OnInit, OnDestroy {
    selectedOrg: OrganizationTo;
    loading = true;
    isDisabled = false;
    isCreateMode = true;
    serviceUnreachable = false;
    header = 'PSAP Map Configuration';
    title: string;
    clientId: string;
    mapProfiles: MapProfile[] = [];
    defaultMapProfile: MapProfile;
    loadedPsapProfile: PsapMapProfile;
    customMapInfo: string;
    form: UntypedFormGroup;
    mapViewContainerId: string;
    mapLoaded = false;
    previewNeeded = false;
    usingDefaultProfile = false;
    private static readonly WATCHED_FIELDS = ['mapClientId', 'mapClientSecret', 'mapProfileId'];
    private arcgisRequire: any;
    private mapView: any;
    private basemapGallery: any;
    private layerList: any;
    private searchWidget: any;

    private static readonly ARCGIS_JS_API_URL = 'https://js.arcgis.com/4.30/';
    private static readonly ARCGIS_CSS_URL = 'https://js.arcgis.com/4.30/esri/themes/light/main.css';
    private static readonly ARCGIS_TOKEN_URL = 'https://www.arcgis.com/sharing/rest/oauth2/token';
    private static readonly ARCGIS_SHARING_URL = 'https://www.arcgis.com/sharing/rest';

    constructor(@Inject(MAT_DIALOG_DATA) public data: OrganizationTo,
                public dialogRef: MatDialogRef<MapConfigurationComponent>,
                public esriMapService: EsriMapService,
                public organizationsService: OrganizationsService,
                public homeService: HomeService,
                public sharedService: SharedService,
                private fb: UntypedFormBuilder) {
        this.selectedOrg = this.data;
        this.title = this.data?.name + ' (' + this.data?.clientId + ')';
        this.clientId = this.data?.clientId;
        this.mapViewContainerId = 'map-preview-' + this.sharedService.generateUUID();
        this.form = this.fb.group({
            mapClientId: ['', Validators.required],
            mapClientSecret: ['', Validators.required],
            mapProfileId: ['', Validators.required]
        });
        // Any user edit to a value the preview depends on invalidates the last preview —
        // programmatic updates in populateForm() use emitEvent:false so loading/resetting the
        // form doesn't itself trigger this.
        MapConfigurationComponent.WATCHED_FIELDS.forEach(field => {
            this.form.get(field).valueChanges.subscribe(() => {
                this.previewNeeded = true;
                this.usingDefaultProfile = false;
            });
        });
    }

    get saveDisabled(): boolean {
        return this.isDisabled || this.serviceUnreachable || this.previewNeeded;
    }

    get previewDisabled(): boolean {
        return !this.form.get('mapClientId').value || !this.form.get('mapClientSecret').value;
    }

    ngOnInit() {
        this.isDisabled = !this.homeService.userDetails.roles.includes(Roles.SUPER);
        this.loadCustomMapInfo();
        this.esriMapService.getMapProfiles().subscribe({
            next: profiles => {
                // The default map profile is a shared fallback, not something an individual
                // org should be able to select and be tied to — kept aside for previewing
                // the default psap-profile scenario, but never offered in the dropdown.
                this.defaultMapProfile = profiles.find(profile => profile.default);
                this.mapProfiles = profiles.filter(profile => !profile.default);
                this.loadPsapMapProfile();
            },
            error: error => {
                this.loading = false;
                this.serviceUnreachable = true;
                this.mapProfiles = [];
                this.sharedService.handleErrors(error, null, 'Unable to retrieve map profiles');
            }
        });
    }

    loadCustomMapInfo() {
        this.organizationsService.getServicePreferencesForOrg(this.selectedOrg.clientId).subscribe({
            next: settings => {
                console.debug('Organization preferences for', this.selectedOrg.clientId, settings);
                this.customMapInfo = settings?.['MAP_CUSTOM_MAP_INFO'];
            },
            error: error => {
                // Read-only informational display only — a failure here shouldn't block or
                // alert on top of whatever the map-profile load path already reports.
                console.debug('Unable to retrieve organization preferences for', this.selectedOrg.clientId, error);
            }
        });
    }

    loadPsapMapProfile() {
        this.esriMapService.getPsapMapProfile(this.clientId).subscribe({
            next: psapProfile => {
                this.loadedPsapProfile = psapProfile;
                // A returned profile that is the shared default, or belongs to another org, must be
                // created fresh for this org rather than updated in place.
                this.isCreateMode = !psapProfile?.id || psapProfile.default === true
                    || this.belongsToAnotherOrg(psapProfile);
                this.populateForm(psapProfile);
                this.loading = false;
            },
            error: error => {
                this.isCreateMode = true;
                this.loading = false;
                if (error?.status !== 404) {
                    this.serviceUnreachable = true;
                    this.sharedService.handleErrors(error, null, 'Unable to retrieve the map configuration');
                }
            }
        });
    }

    // Single source of truth for the org-mismatch half of the "not this org's own record" check,
    // shared by isCreateMode (loadPsapMapProfile) and usingDefaultProfile (populateForm) so the
    // two can't silently drift apart. Guards on `id` so it's also safe to call with no profile at
    // all (psapProfile.psapId would otherwise throw on undefined).
    private belongsToAnotherOrg(psapProfile: PsapMapProfile): boolean {
        return !!psapProfile?.id && psapProfile.psapId !== this.selectedOrg.clientId;
    }

    populateForm(psapProfile: PsapMapProfile) {
        // A returned profile that IS the shared default, or belongs to an ancestor org, has real
        // credentials/profile data, but it isn't this org's — leave the fields blank rather than
        // showing someone else's values.
        this.usingDefaultProfile = psapProfile?.default === true || this.belongsToAnotherOrg(psapProfile);
        const source = this.usingDefaultProfile ? undefined : psapProfile;
        this.form.setValue({
            mapClientId: source?.credentialStore?.clientId ?? '',
            mapClientSecret: source?.credentialStore?.clientSecret ?? '',
            mapProfileId: (source?.mapProfile as MapProfile)?.id ?? ''
        }, { emitEvent: false });
        this.previewNeeded = false;
    }

    buildPayload() {
        const payload: any = {
            psapId: this.selectedOrg.clientId,
            mapProfile: { id: this.form.get('mapProfileId').value },
            credentialStore: {
                description: 'AGOL-' + this.selectedOrg.clientId,
                clientId: this.form.get('mapClientId').value,
                clientSecret: this.form.get('mapClientSecret').value
            },
            default: false
        };
        if (!this.isCreateMode) {
            payload.id = this.loadedPsapProfile?.id;
        }
        return payload;
    }

    save() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        this.loading = true;
        const payload = this.buildPayload();
        const save$ = this.isCreateMode
            ? this.esriMapService.createPsapMapProfile(payload)
            : this.esriMapService.updatePsapMapProfile(this.loadedPsapProfile.id, payload);

        save$.subscribe({
            next: () => {
                this.loading = false;
                this.sharedService.showAlert(Alerts.SUCCESS, 'Map configuration saved successfully');
                this.dialogRef.close(true);
            },
            error: error => {
                this.loading = false;
                this.sharedService.handleErrors(error, null, 'Unable to save map configuration');
            }
        });
    }

    reset() {
        this.populateForm(this.loadedPsapProfile);
    }

    close() {
        this.dialogRef.close(false);
    }

    private injectArcgisStylesheet() {
        if (document.head.querySelector('link[href*="arcgis.com"]')) {
            return;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = MapConfigurationComponent.ARCGIS_CSS_URL;
        document.head.appendChild(link);
    }

    loadArcgisApi(): Promise<any> {
        this.injectArcgisStylesheet();
        if (this.arcgisRequire) {
            return Promise.resolve(this.arcgisRequire);
        }
        if ((window as any).require) {
            this.arcgisRequire = (window as any).require;
            return Promise.resolve(this.arcgisRequire);
        }
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = MapConfigurationComponent.ARCGIS_JS_API_URL;
            script.onload = () => {
                this.arcgisRequire = (window as any).require;
                resolve(this.arcgisRequire);
            };
            script.onerror = () => reject(new Error('Failed to load the ArcGIS Maps SDK for JavaScript'));
            document.head.appendChild(script);
        });
    }

    // client_id/client_secret are app credentials for a non-interactive OAuth 2.0
    // client_credentials exchange, not the appId used by an interactive user-login flow —
    // this is what actually feeds both form values to the Identity Manager.
    async getArcgisAccessToken(clientId: string, clientSecret: string): Promise<{ token: string; expires: number }> {
        const body = new URLSearchParams({
            client_id: clientId,
            client_secret: clientSecret,
            grant_type: 'client_credentials',
            f: 'json'
        });
        const response = await fetch(MapConfigurationComponent.ARCGIS_TOKEN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString()
        });
        const data = await response.json();
        if (!response.ok || data?.error) {
            throw new Error(data?.error?.message ?? 'Unable to authenticate with the provided map client id/secret');
        }
        return { token: data.access_token, expires: Date.now() + data.expires_in * 1000 };
    }

    async previewMap() {
        // When the loaded profile is the shared default, there's nothing selectable in the
        // (intentionally blank) form — preview with the default profile's own basemap instead.
        const selectedProfile = this.usingDefaultProfile
            ? this.defaultMapProfile
            : this.mapProfiles.find(profile => profile.id === this.form.get('mapProfileId').value);
        if (!selectedProfile && !this.serviceUnreachable) {
            this.sharedService.showAlert(Alerts.ERROR, 'Select a map profile before previewing');
            return;
        }

        try {
            const arcgisRequire = await this.loadArcgisApi();

            const clientId = this.form.get('mapClientId').value;
            const clientSecret = this.form.get('mapClientSecret').value;
            const accessToken = (clientId && clientSecret)
                ? await this.getArcgisAccessToken(clientId, clientSecret)
                : null;

            arcgisRequire(
                ['esri/WebMap', 'esri/Basemap', 'esri/layers/Layer', 'esri/views/MapView',
                 'esri/widgets/BasemapGallery', 'esri/widgets/LayerList', 'esri/widgets/Search',
                 'esri/identity/IdentityManager'],
                async (WebMap: any, Basemap: any, Layer: any, MapView: any, BasemapGallery: any, LayerList: any,
                       Search: any, esriId: any) => {
                    try {
                        if (accessToken) {
                            esriId.registerToken({
                                server: MapConfigurationComponent.ARCGIS_SHARING_URL,
                                token: accessToken.token,
                                expires: accessToken.expires
                            });
                        }

                        if (this.mapView) {
                            this.mapView.destroy();
                        }
                        this.mapLoaded = false;
                        this.basemapGallery = undefined;
                        this.layerList = undefined;
                        this.searchWidget = undefined;

                        const webMap = selectedProfile
                            ? new WebMap({ portalItem: { id: selectedProfile.defaultBasemap.portalItemId } })
                            : new WebMap({ basemap: 'osm' }); // fallback when the backend is unreachable

                        this.mapView = new MapView({
                            container: this.mapViewContainerId,
                            map: webMap
                        });

                        // The default psap-profile scenario has no org-specific custom layers
                        // to speak of — skip the merge step entirely.
                        if (!this.usingDefaultProfile) {
                            await this.addCustomMapLayers(webMap, Layer, WebMap);
                        }
                        this.fitPreviewExtent();

                        // Both widgets are created up front but start hidden — only one shows
                        // at a time, toggled by the tool icons, to conserve map space. The
                        // gallery is populated from the selected map profile's own basemap
                        // choices, not Esri's default gallery.
                        const galleryBasemaps = (selectedProfile?.gallery ?? []).map((entry: any) =>
                            new Basemap({ portalItem: { id: entry.portalItemId }, title: entry.basemapName }));
                        this.basemapGallery = new BasemapGallery({
                            view: this.mapView,
                            visible: false,
                            ...(galleryBasemaps.length > 0 ? { source: galleryBasemaps } : {})
                        });
                        this.layerList = new LayerList({ view: this.mapView, visible: false });
                        this.searchWidget = new Search({ view: this.mapView, visible: false });
                        this.mapView.ui.add(this.basemapGallery, 'top-right');
                        this.mapView.ui.add(this.layerList, 'top-right');
                        this.mapView.ui.add(this.searchWidget, 'top-right');
                        this.mapLoaded = true;
                        this.previewNeeded = false;
                    } catch (error) {
                        this.mapLoaded = false;
                        this.sharedService.handleErrors(error, null, 'Unable to load the ArcGIS map preview', true);
                    }
                }
            );
        } catch (error) {
            this.mapLoaded = false;
            this.sharedService.handleErrors(error, null, 'Unable to load the ArcGIS map preview', true);
        }
    }

    // Mirrors spatial-insight-viewer-new's MapContext.js layer-merge pattern: optimistically
    // treat customMapInfo as a single layer's portal item id (or ArcGIS Server URL); if that
    // fails because the item is actually a Web Map, load it separately and merge its
    // non-basemap layers onto the preview map instead of replacing the preview map with it.
    // Returns the layers actually added (empty if none), so the caller can decide how to fit
    // the view's extent.
    async addCustomMapLayers(map: any, Layer: any, WebMap: any): Promise<any[]> {
        if (!this.customMapInfo) {
            return [];
        }
        const isUrl = this.customMapInfo.startsWith('http');

        try {
            const layer = isUrl
                ? await Layer.fromArcGISServerUrl({ url: this.customMapInfo })
                : await Layer.fromPortalItem({ portalItem: { id: this.customMapInfo } });
            map.add(layer);
            return [layer];
        } catch (error: any) {
            if (error?.name !== 'portal:unknown-item-type' || error?.details?.type !== 'Web Map') {
                this.sharedService.handleErrors(error, null, 'Unable to load the custom map layers', true);
                return [];
            }
        }

        try {
            const customWebMap = isUrl
                ? new WebMap({ url: this.customMapInfo })
                : new WebMap({ portalItem: { id: this.customMapInfo } });
            await customWebMap.load();
            const layers = customWebMap.layers.filter((layer: any) => !layer.isBasemap);
            await Promise.all(layers.map((layer: any) => layer.load ? layer.load() : Promise.resolve()));
            layers.forEach((layer: any) => map.add(layer));
            return layers;
        } catch (loadError: any) {
            this.sharedService.handleErrors(loadError, null, 'Unable to load the custom map layers', true);
            return [];
        }
    }

    // Always fits the view to a fixed state-level extent centered on Longmont, CO.
    private fitPreviewExtent() {
        this.mapView.goTo({ center: [-105.1019, 40.1672], zoom: 7 }).catch(() => {
            // A goTo can reject if interrupted by further view interaction — not a real failure.
        });
    }

    toggleBasemapGallery() {
        if (!this.basemapGallery) {
            return;
        }
        const showing = !this.basemapGallery.visible;
        this.basemapGallery.visible = showing;
        if (showing) {
            this.hideOtherToolWidgets(this.basemapGallery);
        }
    }

    toggleLayerList() {
        if (!this.layerList) {
            return;
        }
        const showing = !this.layerList.visible;
        this.layerList.visible = showing;
        if (showing) {
            this.hideOtherToolWidgets(this.layerList);
        }
    }

    toggleSearch() {
        if (!this.searchWidget) {
            return;
        }
        const showing = !this.searchWidget.visible;
        this.searchWidget.visible = showing;
        if (showing) {
            this.hideOtherToolWidgets(this.searchWidget);
        }
    }

    // Only one of the layer list, basemap gallery, or search widgets shows at a time.
    private hideOtherToolWidgets(shown: any) {
        [this.basemapGallery, this.layerList, this.searchWidget].forEach(widget => {
            if (widget && widget !== shown) {
                widget.visible = false;
            }
        });
    }

    ngOnDestroy() {
        this.mapView?.destroy();
        this.mapLoaded = false;
        this.basemapGallery = undefined;
        this.layerList = undefined;
        this.searchWidget = undefined;
    }
}
